<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <h1 class="mb-2">
                    <span class="text-gradient">Staking Analytics</span>
                </h1>
                <p class="text-muted mb-3">Validator economics, Jito adoption, DeFi exposure, and stake concentration trends</p>
            </div>
        </div>

        <!-- Hero Stats -->
        <div class="analytics-hero mb-4" v-if="hasSummaryData">
            <div class="analytics-hero-stat" v-if="concentrationData?.current">
                <div class="hero-label">Nakamoto Coefficient</div>
                <div class="hero-value font-mono-num">{{ concentrationData.current.nakamotoCoefficient }}</div>
                <div class="hero-subtitle">validators</div>
            </div>
            <div class="analytics-hero-stat hero-stat--purple" v-if="concentrationData?.current">
                <div class="hero-label">Total Stake</div>
                <div class="hero-value font-mono-num">{{ formatCompact(concentrationData.current.totalStake) }}</div>
                <div class="hero-subtitle">SOL</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue" v-if="jitoData?.summary">
                <div class="hero-label">Active Validators</div>
                <div class="hero-value font-mono-num">{{ formatCompact(jitoData.summary.totalValidators) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green" v-if="jitoData?.summary">
                <div class="hero-label">Jito Adoption</div>
                <div class="hero-value font-mono-num">{{ jitoData.summary.adoptionRate }}%</div>
                <div class="hero-subtitle">of validators</div>
            </div>
            <div class="analytics-hero-stat hero-stat--cyan" v-if="defiData?.network">
                <div class="hero-label">Network DEX Volume</div>
                <div class="hero-value font-mono-num">{{ formatUsd(defiData.network.totalSwapVolume) }}</div>
                <div class="hero-subtitle">{{ defiRange }}</div>
            </div>
        </div>

        <!-- Section 1: Validator Economics Scatter Plot -->
        <div class="section-divider">
            <h2 class="section-title">Validator Economics</h2>
            <p class="section-subtitle">Stake vs revenue — bubble size represents APY</p>
        </div>

        <div class="analytics-panel mb-4">
            <div class="panel-body">
                <div v-if="economicsLoading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <div v-else-if="economicsData?.validators?.length">
                    <div class="d-flex gap-3 align-items-center flex-wrap mb-3">
                        <div class="btn-group">
                            <button
                                v-for="opt in epochOptions"
                                :key="opt.value"
                                class="compass-btn"
                                :class="{ 'compass-btn--primary': economicsEpochs === opt.value, 'compass-btn--outline': economicsEpochs !== opt.value }"
                                @click="economicsEpochs = opt.value"
                            >{{ opt.label }}</button>
                        </div>
                        <span class="text-muted ms-auto" v-if="economicsData?.epoch">
                            Epoch {{ economicsData.startEpoch }}–{{ economicsData.epoch }}
                        </span>
                    </div>
                    <div class="compass-chart-container">
                        <canvas ref="scatterCanvas" height="400"></canvas>
                    </div>
                    <div class="compass-info-box mt-3">
                        <p class="mb-0"><strong>How to read:</strong> Each bubble is a validator. X-axis = active stake (SOL), Y-axis = total revenue (SOL). Bubble size = APY. Hover for details.</p>
                    </div>
                </div>
                <div v-else class="compass-info-box compass-info-box--warning">
                    <p class="mb-0">Validator economics data is not yet available. This section requires epoch-level staking revenue data to populate.</p>
                </div>
            </div>
        </div>

        <!-- Section 2: Jito Adoption Trend -->
        <div class="section-divider">
            <h2 class="section-title">Jito Adoption</h2>
            <p class="section-subtitle">Percentage of validators receiving Jito tips over time</p>
        </div>

        <div class="analytics-panel mb-4">
            <div class="panel-body">
                <div class="d-flex gap-3 align-items-center flex-wrap mb-3">
                    <div class="btn-group">
                        <button
                            v-for="opt in rangeOptions"
                            :key="opt.value"
                            class="compass-btn"
                            :class="{ 'compass-btn--primary': jitoRange === opt.value, 'compass-btn--outline': jitoRange !== opt.value }"
                            @click="jitoRange = opt.value"
                        >{{ opt.label }}</button>
                    </div>
                </div>
                <div v-if="jitoLoading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <div v-else-if="jitoData?.timeSeries?.length">
                    <div class="analytics-row cols-3 mb-3">
                        <div class="analytics-hero-stat">
                            <div class="hero-label">Jito Validators</div>
                            <div class="hero-value font-mono-num">{{ jitoData.summary.jitoValidators }}</div>
                        </div>
                        <div class="analytics-hero-stat hero-stat--green">
                            <div class="hero-label">Adoption Rate</div>
                            <div class="hero-value font-mono-num">{{ jitoData.summary.adoptionRate }}%</div>
                        </div>
                        <div class="analytics-hero-stat hero-stat--blue">
                            <div class="hero-label">Total Validators</div>
                            <div class="hero-value font-mono-num">{{ jitoData.summary.totalValidators }}</div>
                        </div>
                    </div>
                    <div class="compass-chart-container">
                        <canvas ref="jitoCanvas" height="300"></canvas>
                    </div>
                </div>
                <div v-else class="compass-info-box compass-info-box--warning">
                    <p class="mb-0">No Jito adoption data available for the selected period.</p>
                </div>
            </div>
        </div>

        <!-- Section 3: DeFi Exposure Rankings -->
        <div class="section-divider">
            <h2 class="section-title">DeFi Exposure Rankings</h2>
            <p class="section-subtitle">Which validators process the most DeFi transactions</p>
        </div>

        <div class="analytics-panel mb-4">
            <div class="panel-body">
                <div class="d-flex gap-3 align-items-center flex-wrap mb-3">
                    <div class="btn-group">
                        <button
                            v-for="opt in rangeOptions"
                            :key="opt.value"
                            class="compass-btn"
                            :class="{ 'compass-btn--primary': defiRange === opt.value, 'compass-btn--outline': defiRange !== opt.value }"
                            @click="defiRange = opt.value"
                        >{{ opt.label }}</button>
                    </div>
                </div>
                <div v-if="defiLoading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <div v-else-if="defiData?.validators?.length">
                    <div class="table-responsive">
                        <table class="compass-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Validator</th>
                                    <th class="text-end">Blocks</th>
                                    <th class="text-end">Swap Volume</th>
                                    <th class="text-end">Traders</th>
                                    <th class="text-end">Revenue</th>
                                    <th class="text-end">DeFi Score</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(v, idx) in visibleDefiValidators" :key="v.leader">
                                    <td class="td-num font-mono-num">{{ idx + 1 }}</td>
                                    <td>
                                        <a :href="`/analytics/validators/${v.leader}`" class="text-gradient">
                                            {{ v.name || formatPubkey(v.leader) }}
                                        </a>
                                        <span class="badge bg-secondary ms-2 font-mono-num" v-if="v.blockShare > 1">
                                            {{ v.blockShare.toFixed(1) }}%
                                        </span>
                                    </td>
                                    <td class="td-num text-end font-mono-num">{{ formatCompact(v.blocks) }}</td>
                                    <td class="td-num text-end font-mono-num">
                                        <span class="text-gradient">{{ formatUsd(v.swapVolume) }}</span>
                                        <br>
                                        <small class="text-muted">{{ v.swapVolumeShare.toFixed(2) }}%</small>
                                    </td>
                                    <td class="td-num text-end font-mono-num">{{ formatCompact(v.traders) }}</td>
                                    <td class="td-num text-end font-mono-num">{{ formatSOL(v.revenue) }}</td>
                                    <td class="text-end">
                                        <div class="defi-score-badge font-mono-num" :class="getScoreClass(v.defiScore)">
                                            {{ v.defiScore }}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="text-center mt-3" v-if="hasMoreDefi">
                        <button class="compass-btn compass-btn--outline" @click="defiShowAll = !defiShowAll">
                            {{ defiShowAll ? 'Show Top 20' : `Show All ${defiData.validators.length} Validators` }}
                        </button>
                    </div>
                </div>
                <div v-else class="compass-info-box compass-info-box--warning">
                    <p class="mb-0">No DeFi exposure data available for the selected period.</p>
                </div>
            </div>
        </div>

        <!-- Section 4: Stake Concentration -->
        <div class="section-divider">
            <h2 class="section-title">Stake Concentration</h2>
            <p class="section-subtitle">Nakamoto coefficient and top validator stake share over time</p>
        </div>

        <div class="analytics-panel mb-4">
            <div class="panel-body">
                <div v-if="concentrationLoading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <div v-else-if="concentrationData?.timeSeries?.length">
                    <div class="analytics-row cols-2 mb-3">
                        <div class="compass-chart-container">
                            <div class="chart-title">Nakamoto Coefficient</div>
                            <div class="chart-subtitle">Minimum validators needed for 33.3% of stake</div>
                            <canvas ref="nakamotoCanvas" height="280"></canvas>
                        </div>
                        <div class="compass-chart-container">
                            <div class="chart-title">Top Validator Stake Share</div>
                            <div class="chart-subtitle">Percentage of total stake held by top validators</div>
                            <canvas ref="concentrationCanvas" height="280"></canvas>
                        </div>
                    </div>

                    <!-- Top 10 Validators Table -->
                    <div class="mt-4" v-if="concentrationData.current?.topValidators?.length">
                        <h5 class="text-gradient mb-3">Current Top 10 Validators by Stake</h5>
                        <div class="table-responsive">
                            <table class="compass-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Validator</th>
                                        <th class="text-end">Stake (SOL)</th>
                                        <th class="text-end">Share</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(v, idx) in concentrationData.current.topValidators" :key="v.validator">
                                        <td class="td-num font-mono-num">{{ idx + 1 }}</td>
                                        <td>
                                            <a :href="`/analytics/validators/${v.validator}`" class="text-gradient">
                                                {{ v.name || formatPubkey(v.validator) }}
                                            </a>
                                            <small class="text-muted ms-2" v-if="v.entity">{{ v.entity }}</small>
                                        </td>
                                        <td class="td-num text-end font-mono-num">{{ formatCompact(v.stake) }}</td>
                                        <td class="td-num text-end font-mono-num">{{ v.share }}%</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div v-else class="compass-info-box compass-info-box--warning">
                    <p class="mb-0">Stake concentration data requires epoch-level stake snapshots which are not yet available in this environment.</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js'
Chart.register(...registerables)

export default {
    name: 'StakingAnalytics',

    computed: {
        hasSummaryData() {
            return this.concentrationData?.current || this.jitoData?.summary || this.defiData?.network;
        },
        visibleDefiValidators() {
            const all = this.defiData?.validators || [];
            return this.defiShowAll ? all : all.slice(0, 20);
        },
        hasMoreDefi() {
            return (this.defiData?.validators?.length || 0) > 20;
        },
    },

    data() {
        return {
            // Economics
            economicsLoading: true,
            economicsData: null,
            economicsEpochs: 1,
            epochOptions: [
                { value: 1, label: '1 Epoch' },
                { value: 3, label: '3 Epochs' },
                { value: 5, label: '5 Epochs' },
            ],

            // Jito
            jitoLoading: true,
            jitoData: null,
            jitoRange: '30d',

            // DeFi
            defiLoading: true,
            defiData: null,
            defiRange: '7d',
            defiShowAll: false,

            // Concentration
            concentrationLoading: true,
            concentrationData: null,

            rangeOptions: [
                { value: '7d', label: '7 Days' },
                { value: '30d', label: '30 Days' },
            ],

            // Chart instances
            scatterChart: null,
            jitoChart: null,
            nakamotoChart: null,
            concentrationChart: null,
        };
    },

    mounted() {
        this.loadAll();
        this.refreshInterval = setInterval(() => this.loadAll(), 60000);
    },

    watch: {
        economicsEpochs() { this.loadEconomics(); },
        jitoRange() { this.loadJito(); },
        defiRange() { this.loadDefi(); },
    },

    beforeUnmount() {
        if (this.refreshInterval) clearInterval(this.refreshInterval);
        if (this.scatterChart) this.scatterChart.destroy();
        if (this.jitoChart) this.jitoChart.destroy();
        if (this.nakamotoChart) this.nakamotoChart.destroy();
        if (this.concentrationChart) this.concentrationChart.destroy();
    },

    methods: {
        async loadAll() {
            await Promise.all([
                this.loadEconomics(),
                this.loadJito(),
                this.loadDefi(),
                this.loadConcentration(),
            ]);
        },

        async loadEconomics() {
            this.economicsLoading = true;
            try {
                const res = await fetch(`/analytics/api/staking-economics?epochs=${this.economicsEpochs}`);
                this.economicsData = await res.json();
                this.$nextTick(() => this.renderScatterChart());
            } catch (e) {
                console.error('Error loading economics:', e);
            } finally {
                this.economicsLoading = false;
            }
        },

        async loadJito() {
            this.jitoLoading = true;
            try {
                const res = await fetch(`/analytics/api/staking-jito?range=${this.jitoRange}`);
                this.jitoData = await res.json();
                this.$nextTick(() => this.renderJitoChart());
            } catch (e) {
                console.error('Error loading jito:', e);
            } finally {
                this.jitoLoading = false;
            }
        },

        async loadDefi() {
            this.defiLoading = true;
            this.defiShowAll = false;
            try {
                const res = await fetch(`/analytics/api/staking-defi?range=${this.defiRange}&limit=50`);
                this.defiData = await res.json();
            } catch (e) {
                console.error('Error loading defi:', e);
            } finally {
                this.defiLoading = false;
            }
        },

        async loadConcentration() {
            this.concentrationLoading = true;
            try {
                const res = await fetch(`/analytics/api/staking-concentration?epochs=20`);
                this.concentrationData = await res.json();
                this.$nextTick(() => {
                    this.renderNakamotoChart();
                    this.renderConcentrationChart();
                });
            } catch (e) {
                console.error('Error loading concentration:', e);
            } finally {
                this.concentrationLoading = false;
            }
        },

        renderScatterChart() {
            if (this.scatterChart) this.scatterChart.destroy();
            const canvas = this.$refs.scatterCanvas;
            if (!canvas || !this.economicsData?.validators?.length) return;

            const validators = this.economicsData.validators
                .filter(v => v.stake > 0 && v.totalRevenue > 0);

            const data = validators.map(v => ({
                x: v.stake,
                y: v.totalRevenue,
                r: Math.max(3, Math.min(20, (v.apy || 5) * 2)),
                label: v.name || this.formatPubkey(v.voteAccount),
                apy: v.apy,
                commission: v.commission,
                voteAccount: v.voteAccount,
            }));

            this.scatterChart = new Chart(canvas, {
                type: 'bubble',
                data: {
                    datasets: [{
                        label: 'Validators',
                        data: data,
                        backgroundColor: 'rgba(139, 92, 246, 0.4)',
                        borderColor: 'rgba(139, 92, 246, 0.8)',
                        borderWidth: 1,
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => {
                                    const d = ctx.raw;
                                    const lines = [
                                        d.label,
                                        `Stake: ${this.formatCompact(d.x)} SOL`,
                                        `Revenue: ${this.formatSOL(d.y)}`,
                                    ];
                                    if (d.apy) lines.push(`APY: ${d.apy}%`);
                                    if (d.commission !== null) lines.push(`Commission: ${d.commission}%`);
                                    return lines;
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            type: 'logarithmic',
                            title: { display: true, text: 'Active Stake (SOL)', color: '#9ca3af' },
                            grid: { color: 'rgba(139, 92, 246, 0.1)' },
                            ticks: {
                                color: '#9ca3af',
                                callback: (v) => this.formatCompact(v)
                            }
                        },
                        y: {
                            type: 'logarithmic',
                            title: { display: true, text: 'Total Revenue (SOL)', color: '#9ca3af' },
                            grid: { color: 'rgba(139, 92, 246, 0.1)' },
                            ticks: {
                                color: '#9ca3af',
                                callback: (v) => this.formatSOL(v)
                            }
                        }
                    }
                }
            });
        },

        renderJitoChart() {
            if (this.jitoChart) this.jitoChart.destroy();
            const canvas = this.$refs.jitoCanvas;
            if (!canvas || !this.jitoData?.timeSeries?.length) return;

            const colors = window.divergentColor || ['#9945ff', '#10f7cf'];
            const series = this.jitoData.timeSeries;
            const labels = series.map(d => d.date);

            this.jitoChart = new Chart(canvas, {
                type: 'line',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'Adoption Rate (%)',
                            data: series.map(d => d.adoptionRate),
                            borderColor: colors[0],
                            backgroundColor: colors[0] + '1a',
                            fill: true,
                            tension: 0.3,
                            yAxisID: 'y',
                        },
                        {
                            label: 'Jito Tips (SOL)',
                            data: series.map(d => d.jitoTips),
                            borderColor: '#10f7cf',
                            backgroundColor: '#10f7cf1a',
                            fill: false,
                            tension: 0.3,
                            yAxisID: 'y1',
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                        legend: {
                            labels: { color: '#9ca3af' }
                        }
                    },
                    scales: {
                        x: {
                            grid: { color: colors[0] + '1a' },
                            ticks: { color: '#9ca3af', maxTicksLimit: 10 }
                        },
                        y: {
                            position: 'left',
                            title: { display: true, text: 'Adoption %', color: '#9ca3af' },
                            grid: { color: colors[0] + '1a' },
                            ticks: { color: '#9ca3af' },
                            min: 0,
                        },
                        y1: {
                            position: 'right',
                            title: { display: true, text: 'Tips (SOL)', color: '#9ca3af' },
                            grid: { drawOnChartArea: false },
                            ticks: {
                                color: '#9ca3af',
                                callback: (v) => this.formatCompact(v)
                            },
                        }
                    }
                }
            });
        },

        renderNakamotoChart() {
            if (this.nakamotoChart) this.nakamotoChart.destroy();
            const canvas = this.$refs.nakamotoCanvas;
            if (!canvas || !this.concentrationData?.timeSeries?.length) return;

            const colors = window.divergentColor || ['#9945ff'];
            const series = this.concentrationData.timeSeries;

            this.nakamotoChart = new Chart(canvas, {
                type: 'line',
                data: {
                    labels: series.map(d => `E${d.epoch}`),
                    datasets: [{
                        label: 'Nakamoto Coefficient',
                        data: series.map(d => d.nakamotoCoefficient),
                        borderColor: colors[0],
                        backgroundColor: colors[0] + '1a',
                        fill: true,
                        tension: 0.3,
                        pointRadius: 3,
                        pointBackgroundColor: colors[0],
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => `${ctx.parsed.y} validators needed for 33.3% stake`
                            }
                        }
                    },
                    scales: {
                        x: {
                            grid: { color: colors[0] + '1a' },
                            ticks: { color: '#9ca3af' }
                        },
                        y: {
                            grid: { color: colors[0] + '1a' },
                            ticks: { color: '#9ca3af' },
                            title: { display: true, text: 'Validators', color: '#9ca3af' }
                        }
                    }
                }
            });
        },

        renderConcentrationChart() {
            if (this.concentrationChart) this.concentrationChart.destroy();
            const canvas = this.$refs.concentrationCanvas;
            if (!canvas || !this.concentrationData?.timeSeries?.length) return;

            const colors = window.divergentColor || ['#9945ff', '#608aff'];
            const warmColors = window.palleteColor || ['#a527b3', '#f50b80'];
            const series = this.concentrationData.timeSeries;

            this.concentrationChart = new Chart(canvas, {
                type: 'line',
                data: {
                    labels: series.map(d => `E${d.epoch}`),
                    datasets: [
                        {
                            label: 'Top 20 Share',
                            data: series.map(d => d.top20Share),
                            borderColor: colors[0],
                            backgroundColor: colors[0] + '0d',
                            fill: true,
                            tension: 0.3,
                        },
                        {
                            label: 'Top 10 Share',
                            data: series.map(d => d.top10Share),
                            borderColor: warmColors[2] || '#f50b80',
                            backgroundColor: (warmColors[2] || '#f50b80') + '1a',
                            fill: true,
                            tension: 0.3,
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            labels: { color: '#9ca3af' }
                        }
                    },
                    scales: {
                        x: {
                            grid: { color: colors[0] + '1a' },
                            ticks: { color: '#9ca3af' }
                        },
                        y: {
                            grid: { color: colors[0] + '1a' },
                            ticks: { color: '#9ca3af', callback: v => v + '%' },
                            title: { display: true, text: '% of Total Stake', color: '#9ca3af' }
                        }
                    }
                }
            });
        },

        // Formatters
        formatCompact(v) {
            if (!v) return '0';
            if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B';
            if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M';
            if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
            return v.toLocaleString();
        },
        formatUsd(v) {
            if (!v) return '$0';
            if (v >= 1e9) return '$' + (v / 1e9).toFixed(2) + 'B';
            if (v >= 1e6) return '$' + (v / 1e6).toFixed(2) + 'M';
            if (v >= 1e3) return '$' + (v / 1e3).toFixed(1) + 'K';
            return '$' + v.toFixed(2);
        },
        formatSOL(v) {
            if (!v) return '0 SOL';
            if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M SOL';
            if (v >= 1000) return (v / 1000).toFixed(1) + 'K SOL';
            if (v >= 1) return v.toFixed(2) + ' SOL';
            return v.toFixed(4) + ' SOL';
        },
        formatPubkey(a) {
            return a && a.length > 12 ? `${a.slice(0, 4)}..${a.slice(-4)}` : a;
        },
        getScoreClass(score) {
            if (score >= 80) return 'score-high';
            if (score >= 50) return 'score-medium';
            return 'score-low';
        },
    }
};
</script>

<style scoped>
.defi-score-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: var(--compass-radius-sm);
    font-weight: 600;
    font-size: 0.875rem;
}

.score-high {
    background: var(--compass-gradient-success);
    color: var(--compass-success);
}

.score-medium {
    background: rgba(255, 166, 0, 0.15);
    color: #ffa600;
}

.score-low {
    background: var(--compass-gradient-danger);
    color: var(--compass-danger);
}
</style>
